import { MarketButton } from '@market/react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OneTrustConsent } from '@squareup/service-one-trust';
import { getPersonEmploymentSession } from '@squareup/service-session';
import { readString, writeString } from '@squareup/util-local-storage';
import styles from './cash-local-preview-nux.module.css';

export interface CashLocalPreviewNuxProps {
  /** Whether the NUX should be displayed */
  readonly shouldDisplay?: boolean;

  /** Function to handle navigation to complete profile */
  readonly onCompleteProfile?: () => void;
}

export function CashLocalPreviewNux({
  shouldDisplay = false,
  onCompleteProfile = () => {},
}: CashLocalPreviewNuxProps = {}) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const session = getPersonEmploymentSession();
    const merchantId = session?.merchantToken;

    // Check if NUX should be hidden via localStorage with OneTrust consent
    if (merchantId) {
      const nuxKey = `${merchantId}.cashLocalPreviewNux`;
      const { success, value } = readString(nuxKey, OneTrustConsent.FUNCTIONAL);
      const isHidden = success && value === 'true';
      if (isHidden) {
        setIsVisible(false);
        return;
      }
    }

    // Use the shouldDisplay prop to determine visibility
    setIsVisible(shouldDisplay);
  }, [shouldDisplay]);

  const hideNux = (updateVisibility = false) => {
    const session = getPersonEmploymentSession();
    const merchantId = session?.merchantToken;

    if (merchantId) {
      const nuxKey = `${merchantId}.cashLocalPreviewNux`;
      // Use OneTrust-compliant localStorage utility
      writeString(nuxKey, 'true', OneTrustConsent.FUNCTIONAL);
    }

    if (updateVisibility) {
      setIsVisible(false);
    }
  };

  const handleCompleteProfile = () => {
    hideNux();
    onCompleteProfile();
  };

  const handleNotNow = () => {
    hideNux(true);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.nuxContainer}>
      <div className={styles.iconContainer}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill=" #00E013"
        >
          <path d="M13.9997 0.666626C21.3634 0.666626 27.3335 6.63599 27.3337 13.9996C27.3337 21.3634 21.3635 27.3336 13.9997 27.3336C6.63603 27.3334 0.666672 21.3633 0.666672 13.9996C0.666848 6.6361 6.63614 0.666802 13.9997 0.666626ZM11.9997 15.9518L9.0114 12.4664L6.98796 14.2008L10.988 18.8668L11.0885 18.9723C11.3342 19.2025 11.6594 19.3335 11.9997 19.3336C12.3889 19.3336 12.7581 19.1623 13.0114 18.8668L21.0114 9.53381L18.988 7.79944L11.9997 15.9518Z" fill="#00E013" />
        </svg>
      </div>
      <div className={styles.contentArea}>
        <h3 className={styles.nuxTitle}>
          {t('cashLocalPreviewNux.titleMenu')}
        </h3>
        <p className={styles.nuxContent}>
          {t('cashLocalPreviewNux.descriptionMenu')}
        </p>
        <div className={styles.nuxButtonGroup}>
          <MarketButton
            className={styles.nuxButtonCompleteProfile}
            rank="primary"
            size="small"
            onClick={handleCompleteProfile}
          >
            {t('cashLocalPreviewNux.completeProfile')}
          </MarketButton>
          <MarketButton
            className={styles.nuxButtonNotNow}
            rank="secondary"
            size="small"
            onClick={handleNotNow}
          >
            {t('cashLocalPreviewNux.notNow')}
          </MarketButton>
        </div>
      </div>
    </div>
  );
}
